import { cn } from "@ui/utils"
import { cva, type VariantProps } from "class-variance-authority"
import { Building2, Home, User } from "lucide-react"

const chipVariants = cva("inline-flex min-w-0 items-center gap-1 text-sm font-medium text-tertiary-foreground", {
  variants: {
    variant: {
      default: "",
      pill: "rounded-full border border-border bg-background px-2.5 py-0.5",
      "clickable-pill":
        "rounded-full border border-border bg-background px-2.5 py-0.5 cursor-pointer hover:bg-accent transition-colors",
    },
    size: {
      xs: "text-xs",
      sm: "text-sm",
      md: "text-sm",
    },
  },
  defaultVariants: {
    variant: "clickable-pill",
    size: "sm",
  },
})

export interface CrossReferenceValue {
  type: string
  id: number
  label?: string
  household_type?: string
}

interface CrossReferenceChipProps extends VariantProps<typeof chipVariants> {
  value: CrossReferenceValue | null
  className?: string
}

export function CrossReferenceChip({ value, variant, size, className }: CrossReferenceChipProps) {
  if (!value) {
    return <span className="text-muted-foreground text-xs">-</span>
  }

  const getIcon = () => {
    switch (value.type) {
      case "User":
        return <User className="size-3.5 shrink-0" />
      case "Household":
        return <Home className="size-3.5 shrink-0" />
      case "Business":
        return <Building2 className="size-3.5 shrink-0" />
      default:
        return null
    }
  }

  const getLabel = () => {
    // Use the label from backend if available, otherwise fallback
    if (value.label) {
      return value.label
    }
    // Fallback for backward compatibility
    return `${value.type} ${value.id}`
  }

  return (
    <div className={cn(chipVariants({ variant, size }), className)}>
      {getIcon()}
      <span className="truncate">{getLabel()}</span>
    </div>
  )
}
